import { BaseError } from "@/domains/errors";
import type { INoteObservable } from "@/store/note/types";
import { INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT } from "@/store/sync/operations/notes/CreateNoteOperation";
import { noteModule, syntaxTreeModule } from "@mem-labs/common-editor";

export enum NoteSuggestionContentState {
  Ready = "ready",
  WaitingForRemote = "waiting_for_remote",
  MissingNote = "missing_note",
}

export type NoteSuggestionContentReadiness =
  | { state: NoteSuggestionContentState.Ready; source: "remote" | "local" }
  | { state: NoteSuggestionContentState.WaitingForRemote }
  | { state: NoteSuggestionContentState.MissingNote };

export type NoteSuggestionBaseContent = {
  encodedContent: string;
  markdownContent: string;
  primaryLabel: string;
  secondaryLabel: string;
  isEmpty: boolean;
  source: "remote" | "local";
};

export const getNoteSuggestionContentReadiness = (
  note?: INoteObservable | null
): NoteSuggestionContentReadiness => {
  if (!note) return { state: NoteSuggestionContentState.MissingNote };

  const existsRemotely =
    "existsRemotely" in note
      ? Boolean((note as { existsRemotely?: boolean }).existsRemotely)
      : true;

  if (existsRemotely && !note.isNoteContentDocumentLoaded) {
    return { state: NoteSuggestionContentState.WaitingForRemote };
  }

  return { state: NoteSuggestionContentState.Ready, source: existsRemotely ? "remote" : "local" };
};

const convertEncodedContentToMarkdown = ({
  encodedContent,
  noteId,
}: {
  encodedContent: string;
  noteId: string;
}): string => {
  if (!encodedContent) return "";

  try {
    const yDoc = syntaxTreeModule.convertEncodedContentToYDoc({ encodedContent });
    return syntaxTreeModule.convertYDocToMdxContent({ yDoc });
  } catch (originalError) {
    throw new BaseError({
      message: "Failed to decode note content for suggestion processing",
      info: {
        noteId,
      },
      originalError: originalError instanceof Error ? originalError : undefined,
    });
  }
};

const extractEncodedContentMetadata = ({
  encodedContent,
  note,
}: {
  encodedContent: string;
  note: Pick<INoteObservable, "title" | "secondaryTitle">;
}) => {
  try {
    const { primaryLabel, secondaryLabel, isEmpty } = noteModule.extractContentFromEncodedContent({
      encodedContent,
    });

    return {
      primaryLabel: primaryLabel || "",
      secondaryLabel: secondaryLabel || "",
      isEmpty,
    };
  } catch {
    const isEmpty =
      !encodedContent || encodedContent === INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT;

    return {
      primaryLabel: isEmpty ? "" : note.title || "",
      secondaryLabel: note.secondaryTitle || "",
      isEmpty,
    };
  }
};

export const resolveNoteSuggestionBaseContent = async (
  note: INoteObservable
): Promise<NoteSuggestionBaseContent> => {
  let readiness = getNoteSuggestionContentReadiness(note);
  if (readiness.state === NoteSuggestionContentState.WaitingForRemote) {
    await note.fetchNoteContentDocument();
    readiness = getNoteSuggestionContentReadiness(note);
  }

  if (readiness.state !== NoteSuggestionContentState.Ready) {
    const message =
      readiness.state === NoteSuggestionContentState.WaitingForRemote
        ? "Waiting for note to sync..."
        : "Note content not ready for suggestion processing";
    throw new BaseError({
      message,
      info: { noteId: note?.id, readiness: readiness.state },
    });
  }

  const baseEncodedContent =
    readiness.source === "remote"
      ? (await note.fetchNoteContentDocument(), note.remoteContent)
      : INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT;

  if (!baseEncodedContent) {
    throw new BaseError({
      message: "Note content not loaded from server yet",
      info: { noteId: note.id },
    });
  }

  const queuedUpdates = note.queuedDocumentUpdates;
  const mergedEncodedContent = noteModule.resolveEncodedContentFromUpdates({
    baseEncodedContent,
    updates: queuedUpdates.map(update => ({
      encodedContentDiff: update.encodedContentDiff,
      isDestructive: update.isDestructive,
    })),
  });
  const { primaryLabel, secondaryLabel, isEmpty } = extractEncodedContentMetadata({
    encodedContent: mergedEncodedContent,
    note,
  });

  return {
    encodedContent: mergedEncodedContent,
    markdownContent: convertEncodedContentToMarkdown({
      encodedContent: mergedEncodedContent,
      noteId: note.id,
    }),
    primaryLabel,
    secondaryLabel,
    isEmpty,
    source: readiness.source,
  };
};
